import clsx from "clsx";
import React from "react";
import { Box, CardProps, Card, Typography, Grid2 } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { colorSystem } from "../../theme";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { UserRole } from "../../constants";
import { useNavigate } from "react-router-dom";
import { Icons } from "../plate-ui/Icons/icons";
import SeeMoreNotes from "./SeeMoreNotes";
import { Brain, BicepsFlexed, Frown, Smile, Clock } from "lucide-react";
import MinimizedTooltip from "../tooltip/MinimizedTooltip";

export const FEEL_MAP: Record<string, any> = {
  "5 V.Hard": { class: "bad", icon: Frown },
  "4 Hard": { class: "tired", icon: Frown },
  "1 V.Ease": { class: "hyped", icon: Smile },
  "2 Ease": { class: "good", icon: Smile },
  "3 Medium": { class: "ok", icon: Smile },
};

const useStyles = (shortened: boolean) =>
  makeStyles(({ spacing, palette }) => {
    return {
      root: {
        ...(!shortened
          ? {
              padding: spacing(3),
              marginBottom: spacing(2),
            }
          : {
              display: "flex",
              alignItems: "center",
              gap: spacing(2),
            }),
      },

      title: {
        fontFamily: "Montserrat, sans-serif",
        fontSize: 18,
        color: palette.text.secondary,
        fontWeight: 500,
        marginBottom: spacing(2),
        display: "block",
      },

      type: {
        fontFamily: "Montserrat, sans-serif",
        marginLeft: spacing(4),
      },

      notes: {
        ...(shortened
          ? {
              fontSize: 14,
              fontWeight: "normal",
              wordBreak: "break-word",
            }
          : {
              backgroundColor: palette.action.hover,
              padding: spacing(1.5, 2),
              borderRadius: 8,
              fontSize: 16,
              fontWeight: "normal",
              wordBreak: "break-word",
            }),
      },

      value: {
        fontFamily: "Montserrat, sans-serif",
        fontWeight: shortened ? 600 : 500,
        fontSize: shortened ? 14 : 18,
      },

      hyped: {
        color: colorSystem.green2,
      },
      bad: {
        color: colorSystem.primary,
      },
      tired: {
        color: colorSystem.orange,
      },
      ok: {
        color: colorSystem.yellow,
      },
      good: {
        color: colorSystem.blue1,
      },

      item: {
        borderColor: palette.divider,
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 40,
        backgroundColor: palette.action.hover,
        padding: shortened ? spacing(1, 2) : spacing(0, 3, 0, 0),
        display: "flex",
        alignItems: "center",
        justifyContent: shortened ? "flex-start" : "space-between",
        columnGap: shortened ? spacing(2) : 0,
        marginBottom: spacing(1.3),
      },

      bottomContainer: {
        marginTop: spacing(4),
      },

      flex: {
        display: "flex",
        alignItems: "center",
        ...(shortened && { gap: spacing(2) }),
      },

      button: {
        fontFamily: "Montserrat, sans-serif",
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "44px",
        marginTop: spacing(5),
      },

      iconContainer: {
        backgroundColor: palette.background.paper,
        width: shortened ? 40 : 70,
        height: shortened ? 40 : 70,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
      },
      mindIconColor: { color: colorSystem.primary },
      bodyIconColor: { color: colorSystem.blue1 },
      feelIconColor: { color: colorSystem.orange },
      timeIconColor: { color: colorSystem.gray2 },
    };
  });

export interface TrainingSummaryWellBeginProps extends CardProps {
  startWorkout: any;
  endWorkout: any;
  shortened?: boolean;
}

export function TrainingSummaryWellBegin(props: TrainingSummaryWellBeginProps) {
  const {
    className,
    startWorkout,
    endWorkout,
    shortened = false,
    ...other
  } = props;
  const navigate = useNavigate();
  const s = useStyles(shortened)();
  const user = useCurrentUser();

  const isCoach = user.role === UserRole.COACH;

  const styleBefore = (value: string) => {
    switch (value) {
      case "5 Hyped":
      case "5 Strong":
        return s.hyped;
      case "1 Bad":
        return s.bad;
      case "2 Tired":
        return s.tired;
      case "3 OK":
        return s.ok;
      case "4 Good":
        return s.good;
      default:
        return "";
    }
  };

  const styleAfter = (value: string) =>
    FEEL_MAP[value]?.class ? s[FEEL_MAP[value].class] : "";

  const FeelIcon = ({ value, size }: { value: string; size?: number }) => {
    const Icon = FEEL_MAP[value]?.icon || Smile;
    return <Icon size={size || 24} className={s.feelIconColor} />;
  };

  if (shortened) {
    const items = [
      {
        description: "Mental readiness for training",
        element: startWorkout?.mindValue ? (
          <>
            <Brain size={24} className={s.mindIconColor} />
            <Typography
              className={`${s.value} ${styleBefore(startWorkout.mindValue)}`}
              noWrap
            >
              {startWorkout.mindValue}
            </Typography>
          </>
        ) : null,
      },
      {
        description: "Physical readiness for training",
        element: startWorkout.bodyValue ? (
          <>
            <BicepsFlexed size={24} className={s.bodyIconColor} />
            <Typography className={s.value} noWrap>
              {startWorkout.bodyValue}
            </Typography>
          </>
        ) : null,
      },
      {
        description: "Feelings after training",
        element: endWorkout.feel ? (
          <>
            <FeelIcon value={endWorkout.feel} size={24} />
            <Typography
              className={`${s.value} ${styleAfter(endWorkout.feel)}`}
              noWrap
            >
              {endWorkout.feel}
            </Typography>
          </>
        ) : null,
      },
      {
        description: "How long the training took",
        element: endWorkout.time ? (
          <>
            <Clock size={20} className={s.timeIconColor} />
            <Typography className={s.value} noWrap>
              {endWorkout.time} min
            </Typography>
          </>
        ) : null,
      },
      {
        description: "",
        element: endWorkout.notes ? (
          <>
            <Icons.notepadText
              width={20}
              height={20}
              style={{
                flexShrink: 0,
              }}
            />
            <SeeMoreNotes>{endWorkout.notes}</SeeMoreNotes>
          </>
        ) : null,
      },
    ];
    return (
      <Grid2
        container
        columnSpacing={2}
        sx={{
          maxWidth: 700,
        }}
      >
        {items
          // Filter out any null items if values are empty
          .filter((i) => i.element)
          .map((item, index) => (
            <Grid2
              key={index}
              size={{
                xs: index === items.length - 1 ? 12 : 6,
                sm: index === items.length - 1 ? 12 : "auto",
              }}
            >
              <MinimizedTooltip
                title={item.description}
                placement="bottom"
                opacity={0.9}
                delayNextEnter={false}
              >
                <Box
                  className={s.item}
                  sx={{
                    ...(index === items.length - 1 && {
                      borderRadius: "12px !important",
                    }),
                  }}
                >
                  {item.element}
                </Box>
              </MinimizedTooltip>
            </Grid2>
          ))}
      </Grid2>
    );
  }

  return (
    <Card className={clsx(s.root, className)} {...other}>
      <Box>
        <span className={s.title}>Well-being before training</span>
        <Box className={s.item}>
          <div className={s.flex}>
            <div className={s.iconContainer}>
              <Brain size={35} className={s.mindIconColor} />
            </div>
            <Typography variant="h6" className={s.type}>
              Mind
            </Typography>
          </div>
          <span className={`${s.value} ${styleBefore(startWorkout.mindValue)}`}>
            {startWorkout.mindValue}
          </span>
        </Box>
        <Box className={s.item}>
          <div className={s.flex}>
            <div className={s.iconContainer}>
              <BicepsFlexed size={35} className={s.bodyIconColor} />
            </div>
            <Typography variant="h6" className={s.type}>
              Body
            </Typography>
          </div>
          <span className={`${s.value} ${styleBefore(startWorkout.bodyValue)}`}>
            {startWorkout.bodyValue}
          </span>
        </Box>
      </Box>
      <Box className={s.bottomContainer}>
        <span className={s.title}>Well-being after training</span>
        <Box className={s.item}>
          <div className={s.flex}>
            <div className={s.iconContainer}>
              <FeelIcon value={endWorkout.feel} size={35} />
            </div>
            <Typography variant="h6" className={s.type}>
              Feels
            </Typography>
          </div>
          <span className={`${s.value} ${styleAfter(endWorkout.feel)}`}>
            {endWorkout.feel}
          </span>
        </Box>
        <Box className={s.item}>
          <div className={s.flex}>
            <div className={s.iconContainer}>
              <Clock size={35} className={s.timeIconColor} />
            </div>
            <Typography variant="h6" className={s.type}>
              Time
            </Typography>
          </div>
          <span className={s.value}>{endWorkout.time} minutes</span>
        </Box>
      </Box>

      {endWorkout.notes && (
        <Box className={s.bottomContainer}>
          <span className={s.title}>Notes</span>
          <Typography className={`${s.notes}`}>{endWorkout.notes}</Typography>
        </Box>
      )}
    </Card>
  );
}
